import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, C360CardGrid, ColumnChooser, ViewSwitch, DEFAULT_VIEW_MODES, KpiCard, DonutCard,
  ViewIcon, EditIcon, DeleteIcon, CardIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  TYPE_OPTIONS, deriveCampaignKind, buildHumanRule, restrictionSummary, hasRestrictions,
  fmtMoney, fmtDate, csvToArr, resolveLabels,
} from './campaign/campaignUtils';
import { CampaignTypeBadge } from './campaign/CampaignTypeBadge';
import { usePageActions } from '../contexts/PermissionContext';

// NOT: /api/campaigns REST controller'ı (OData GET değil) — ?type/isActive/take ile
// {items, activeCount, inactiveCount} döner. Bu yüzden c360 UI kabuğu + yerel filtre/sıralama/sayfalama.
const API_BASE = '/api/campaigns';
const PAGE_SIZE_DEFAULT = 10;
const FETCH_TAKE = 500;

const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

const COLUMN_FILTER_META = {
  code: { type: 'text', odataField: 'Code' },
  name: { type: 'text', odataField: 'Name' },
  type: { type: 'select', odataField: 'Type', options: TYPE_OPTIONS },
  isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: ACTIVE_OPTIONS },
};
const SEARCH_FIELDS = ['Code', 'Name', 'ShortDescription'];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    affectedCustomersTotal: pick(data, 'affectedCustomersTotal', 'AffectedCustomersTotal') ?? 0,
  };
}

function countCsvIds(csv) {
  return csvToArr(csv).length;
}

function matchColumnFilter(meta, filter, rawValue) {
  if (!meta || !filter) return true;
  if (meta.type === 'text') {
    const needle = String(filter.value ?? '').trim().toLocaleLowerCase('tr-TR');
    if (!needle) return true;
    return String(rawValue ?? '').toLocaleLowerCase('tr-TR').includes(needle);
  }
  if (meta.type === 'select') {
    const target = filter.value;
    if (target === '' || target == null) return true;
    if (meta.valueFormat === 'raw') return String(!!rawValue) === String(target);
    return String(rawValue) === String(target);
  }
  return true;
}

// Liste + Kart + Grafik (kanban yok). Kart, Liste'nin hemen YANINDA (index 1).
const CAMPAIGN_VIEW_MODES = (() => {
  const base = DEFAULT_VIEW_MODES.filter((m) => m.value !== 'kanban');
  const list = base.find((m) => m.value === 'list');
  const rest = base.filter((m) => m.value !== 'list');
  const cardMode = { value: 'card', label: 'Kart', icon: CardIcon };
  return list ? [list, cardMode, ...rest] : [cardMode, ...base];
})();

// ── Trade Marketing panosu stilleri/yardımcıları ──
const tmTbl = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const tmTh = { padding: '8px 10px', borderBottom: '2px solid var(--border-color, #e5e7eb)', color: 'var(--text-secondary, #64748b)', fontWeight: 800, fontSize: 11, whiteSpace: 'nowrap' };
const tmTd = { padding: '8px 10px', borderBottom: '1px solid var(--border-color, #eef2f7)', verticalAlign: 'middle' };
const EFF_BUDGET = (r) => (Number(r.budgetLimit) > 0 ? Number(r.budgetLimit) : 50000);
const pctOf = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);

function BudgetCell({ label, value, pct, accent }) {
  return (
    <div style={{ border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12, padding: '10px 12px', borderTop: `3px solid ${accent}` }}>
      <div style={{ fontSize: 11, color: 'var(--text-secondary, #64748b)', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800, marginTop: 2 }}>{fmtMoney(value)}</div>
      <div style={{ fontSize: 11, color: accent, fontWeight: 800, marginTop: 2 }}>%{pct.toFixed(1)}</div>
    </div>
  );
}

// Grafik görünümü — Trade Marketing Dashboard (promosyon performansı + bütçe takibi).
function CampaignChartView({ rows, affectedCustomersTotal = 0 }) {
  const active = rows.filter((r) => r.isActive);
  const spent = rows.reduce((sum, r) => sum + Number(r.usageAmount || 0), 0);
  const discRows = rows.filter((r) => Number(r.discountPct) > 0);
  const avgDiscount = discRows.length ? discRows.reduce((s, r) => s + Number(r.discountPct), 0) / discRows.length : 0;

  // Bütçe özeti — bütçesi olmayan kampanyalarda etkin ₺50.000 (tek seferlik DB defaultu ile uyumlu).
  const totalBudget = rows.reduce((s, r) => s + EFF_BUDGET(r), 0);
  const used = spent;
  const committed = Math.max(0, active.reduce((s, r) => s + (EFF_BUDGET(r) - Number(r.usageAmount || 0)), 0));
  const remaining = Math.max(0, totalBudget - used - committed);

  const top = [...rows].sort((a, b) => Number(b.usageAmount || 0) - Number(a.usageAmount || 0)).slice(0, 5);

  const kindMap = {};
  rows.forEach((r) => { const k = deriveCampaignKind(r).label; kindMap[k] = (kindMap[k] || 0) + Number(r.usageAmount || 0); });
  const kindData = Object.entries(kindMap).map(([name, value]) => ({ name, value })).filter((d) => d.value > 0).sort((a, b) => b.value - a.value);

  const calendar = active.slice().sort((a, b) => new Date(a.startDate || 0) - new Date(b.startDate || 0));

  return (
    <div className="c360-dashboard">
      <div className="c360-kpi-grid">
        <KpiCard label="Aktif Promosyonlar" value={active.length.toLocaleString('tr-TR')} hint={`${rows.length} kampanya`} />
        <KpiCard label="Harcanan Bütçe" value={fmtMoney(spent)} hint="tüm promosyonlar" />
        <KpiCard label="Etkilenen Müşteri" value={affectedCustomersTotal.toLocaleString('tr-TR')} hint="benzersiz" />
        <KpiCard label="Ortalama İndirim" value={`%${avgDiscount.toFixed(2)}`} hint="% indirimli kampanyalar" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.7fr) minmax(0, 1fr)', gap: 12, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <div className="c360-chart-panel">
            <div className="c360-chart-panel-head">
              <span className="c360-chart-panel-title">En Performanslı Promosyonlar</span>
              <span className="c360-chart-panel-sub">Top {top.length}</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={tmTbl}>
                <thead>
                  <tr>
                    {['Promosyon Adı', 'Tip', 'Kullanım', 'Toplam İndirim', 'Müşteri', 'Bütçe Kullanımı'].map((h, i) => (
                      <th key={h} style={{ ...tmTh, textAlign: i >= 2 && i <= 4 ? 'right' : 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {top.map((r) => {
                    const u = Number(r.usageAmount || 0);
                    const b = EFF_BUDGET(r);
                    const p = pctOf(u, b);
                    const barColor = p >= 80 ? 'var(--danger)' : p >= 40 ? 'var(--warn)' : 'var(--success)';
                    return (
                      <tr key={r.id}>
                        <td style={tmTd}><strong>{r.name}</strong></td>
                        <td style={tmTd}><CampaignTypeBadge kind={deriveCampaignKind(r)} /></td>
                        <td style={{ ...tmTd, textAlign: 'right' }}>{Number(r.usageCount || 0).toLocaleString('tr-TR')}</td>
                        <td style={{ ...tmTd, textAlign: 'right', fontWeight: 700 }}>{fmtMoney(u)}</td>
                        <td style={{ ...tmTd, textAlign: 'right' }}>{Number(r.affectedCustomers || 0).toLocaleString('tr-TR')}</td>
                        <td style={{ ...tmTd, minWidth: 150 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-secondary, #64748b)', marginBottom: 4 }}>
                            <span>{fmtMoney(u)}</span><span>%{p.toFixed(0)}</span>
                          </div>
                          <div style={{ height: 8, borderRadius: 999, background: 'rgba(15,23,42,0.08)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(100, p)}%`, background: barColor, borderRadius: 999 }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {top.length === 0 && <tr><td colSpan={6} style={{ ...tmTd, textAlign: 'center', color: 'var(--text-secondary, #94a3b8)' }}>Kayıt yok</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="c360-chart-panel">
            <div className="c360-chart-panel-head">
              <span className="c360-chart-panel-title">İndirim Dağılımı (Kategori Bazlı)</span>
              <span className="c360-chart-panel-sub">{kindData.length} tür</span>
            </div>
            {kindData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 50, color: 'var(--text-secondary, #94a3b8)', fontWeight: 700, fontSize: 12 }}>Henüz kullanım verisi yok</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={kindData} margin={{ top: 16, right: 8, left: 0, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={11} angle={-20} textAnchor="end" interval={0} height={70} />
                  <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => (v >= 1000 ? `₺${(v / 1000).toFixed(0)}K` : `₺${v}`)} />
                  <Tooltip cursor={{ fill: 'rgba(37,99,235,0.06)' }} formatter={(v) => [fmtMoney(v), 'Toplam İndirim']} />
                  <Bar dataKey="value" fill="#2563eb" radius={[8, 8, 0, 0]}>
                    <LabelList dataKey="value" position="top" formatter={(v) => fmtMoney(v)} style={{ fill: '#334155', fontWeight: 800, fontSize: 10 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <div className="c360-chart-panel">
            <div className="c360-chart-panel-head">
              <span className="c360-chart-panel-title">Promosyon Takvimi</span>
              <span className="c360-chart-panel-sub">Aktif {calendar.length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {calendar.length === 0 && <div style={{ color: 'var(--text-secondary, #94a3b8)', fontWeight: 700, fontSize: 12, padding: '8px 2px' }}>Aktif promosyon yok</div>}
              {calendar.slice(0, 8).map((r) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12 }}>
                  <StatusBadge tone="ok">Aktif</StatusBadge>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary, #64748b)' }}>{fmtDate(r.startDate)} – {fmtDate(r.endDate)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="c360-chart-panel">
            <div className="c360-chart-panel-head">
              <span className="c360-chart-panel-title">Bütçe Kullanım Özeti</span>
              <span className="c360-chart-panel-sub">{fmtMoney(totalBudget)}</span>
            </div>
            <div style={{ display: 'flex', height: 10, borderRadius: 999, overflow: 'hidden', margin: '4px 0 12px' }}>
              <div style={{ width: `${pctOf(used, totalBudget)}%`, background: 'var(--danger)' }} />
              <div style={{ width: `${pctOf(committed, totalBudget)}%`, background: 'var(--warn)' }} />
              <div style={{ flex: 1, background: 'var(--success)' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              <BudgetCell label="Kullanılan" value={used} pct={pctOf(used, totalBudget)} accent="var(--danger)" />
              <BudgetCell label="Taahhüt Edilen" value={committed} pct={pctOf(committed, totalBudget)} accent="var(--warn)" />
              <BudgetCell label="Kalan" value={remaining} pct={pctOf(remaining, totalBudget)} accent="var(--success)" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CampaignsPage() {
  usePageMeta('Kampanya Yönetimi', 'Saha sipariş ekranında otomatik uygulanan kampanyalar.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();
  const [viewMode, setViewMode] = useState('list');

  const [allRows, setAllRows] = useState([]);
  const [affectedCustomersTotal, setAffectedCustomersTotal] = useState(0);
  const [productLabels, setProductLabels] = useState({});
  const [loading, setLoading] = useState(true); // ilk paint'te boş-durum yanıp sönmesin
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);

  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?take=${FETCH_TAKE}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const { items, affectedCustomersTotal: act } = unwrapResponse(payload);
      setAffectedCustomersTotal(Number(act) || 0);
      const mapped = (Array.isArray(items) ? items : []).map((r) => ({
        id: pick(r, 'id', 'Id'),
        code: pick(r, 'code', 'Code') ?? '',
        name: pick(r, 'name', 'Name') ?? '',
        shortDescription: pick(r, 'shortDescription', 'ShortDescription') ?? '',
        type: pick(r, 'type', 'Type') ?? '',
        conditionUnit: pick(r, 'conditionUnit', 'ConditionUnit') ?? 'QUANTITY',
        applicableProductIdsCsv: pick(r, 'applicableProductIdsCsv', 'ApplicableProductIdsCsv') ?? '',
        freeProductIdsCsv: pick(r, 'freeProductIdsCsv', 'FreeProductIdsCsv') ?? '',
        thresholdQty: pick(r, 'thresholdQty', 'ThresholdQty') ?? 0,
        thresholdAmount: pick(r, 'thresholdAmount', 'ThresholdAmount') ?? 0,
        freeQty: pick(r, 'freeQty', 'FreeQty') ?? 0,
        discountPct: pick(r, 'discountPct', 'DiscountPct') ?? 0,
        discountAmount: pick(r, 'discountAmount', 'DiscountAmount') ?? 0,
        maxApplications: pick(r, 'maxApplications', 'MaxApplications') ?? 0,
        maxPerCustomer: pick(r, 'maxPerCustomer', 'MaxPerCustomer') ?? 0,
        maxPerDistributor: pick(r, 'maxPerDistributor', 'MaxPerDistributor') ?? 0,
        maxPerDay: pick(r, 'maxPerDay', 'MaxPerDay') ?? 0,
        budgetLimit: pick(r, 'budgetLimit', 'BudgetLimit') ?? 0,
        usageCount: pick(r, 'usageCount', 'UsageCount') ?? 0,
        usageAmount: pick(r, 'usageAmount', 'UsageAmount') ?? 0,
        affectedCustomers: pick(r, 'affectedCustomers', 'AffectedCustomers') ?? 0,
        startDate: pick(r, 'startDate', 'StartDate') ?? null,
        endDate: pick(r, 'endDate', 'EndDate') ?? null,
        isActive: pick(r, 'isActive', 'IsActive') ?? false,
        createdBy: pick(r, 'createdBy', 'CreatedBy') ?? '',
        createdDate: pick(r, 'createdDate', 'CreatedDate') ?? null,
        updatedDate: pick(r, 'updatedDate', 'UpdatedDate') ?? null,
      }));
      setAllRows(mapped);
      setCurrentPage(1);

      // Detaylar sütununda gerçek ürün adı göstermek için ilk ürünleri çöz.
      const firstIds = Array.from(new Set(mapped.map((r) => csvToArr(r.applicableProductIdsCsv)[0]).filter(Boolean)));
      if (firstIds.length) {
        resolveLabels('/api/products', firstIds).then((map) => setProductLabels(map)).catch(() => {});
      }
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const searchKeys = SEARCH_FIELDS.map((f) => f.charAt(0).toLowerCase() + f.slice(1));
    const matches = (row) => {
      if (needle) {
        const hit = searchKeys.some((k) => String(row[k] ?? '').toLocaleLowerCase('tr-TR').includes(needle));
        if (!hit) return false;
      }
      for (const [field, filter] of Object.entries(columnFilters)) {
        if (!matchColumnFilter(COLUMN_FILTER_META[field], filter, row[field])) return false;
      }
      return true;
    };
    const filtered = allRows.filter(matches);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Code': return String(row.code ?? '');
        case 'Name': return String(row.name ?? '');
        case 'Type': return String(row.type ?? '');
        case 'StartDate': return row.startDate ? new Date(row.startDate).getTime() : 0;
        case 'UpdatedDate':
        default: return row.updatedDate ? new Date(row.updatedDate).getTime() : 0;
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a); const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [allRows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const rows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const applyFilterText = (text) => { setFilterText(text); setCurrentPage(1); };
  const setColumnFilter = (columnId, next) => { setColumnFilters((p) => ({ ...p, [columnId]: next })); setCurrentPage(1); };
  const clearColumnFilter = (columnId) => {
    setColumnFilters((p) => { const c = { ...p }; delete c[columnId]; return c; });
    setCurrentPage(1);
  };
  const applySort = (odataField, dir) => { setSortField(odataField || 'UpdatedDate'); setSortDir(dir || 'asc'); setCurrentPage(1); };

  const openNew = () => navigate('/campaigns/new');
  const openDetail = (row) => { if (row?.id) navigate(`/campaigns/${row.id}`); };

  const handleDelete = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${selected.id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Kampanya silindi.');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setDeleteConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Kampanya', meta: { filterType: 'text' },
      cell: ({ row }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>🎁</span>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0 }}>
            <span style={{ fontWeight: 700 }}>{row.original.name || '-'}</span>
            {row.original.shortDescription && (
              <span style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {row.original.shortDescription}
              </span>
            )}
          </div>
        </div>
      ),
    }),
    columnHelper.accessor('type', {
      header: 'Tür', meta: { filterType: 'select' },
      cell: ({ row }) => <CampaignTypeBadge kind={deriveCampaignKind(row.original)} />,
    }),
    columnHelper.display({
      id: 'details', header: 'Detaylar',
      cell: ({ row }) => {
        const firstId = csvToArr(row.original.applicableProductIdsCsv)[0];
        const count = countCsvIds(row.original.applicableProductIdsCsv);
        const chip = firstId ? (productLabels[firstId] || `${count} ürün`) : (count ? `${count} ürün` : 'Tüm ürünler');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, lineHeight: 1.3 }}>
            <span style={{ fontSize: 13 }}>{buildHumanRule(row.original)}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-info, #185fa5)' }}>
              <span aria-hidden="true">◷</span>{chip}{count > 1 && firstId && productLabels[firstId] ? ` +${count - 1}` : ''}
            </span>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'period', header: 'Geçerlilik',
      cell: ({ row }) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap' }}>
          <span aria-hidden="true">🗓</span>
          {fmtDate(row.original.startDate)} – {fmtDate(row.original.endDate)}
        </span>
      ),
    }),
    columnHelper.accessor('isActive', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => (info.getValue() ? <StatusBadge tone="ok">Aktif</StatusBadge> : <StatusBadge tone="neutral">Pasif</StatusBadge>),
    }),
    columnHelper.display({
      id: 'restrictions', header: 'Kısıtlamalar', enableColumnFilter: false,
      cell: ({ row }) => {
        const has = hasRestrictions(row.original);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <StatusBadge tone={has ? 'wait' : 'neutral'}>{has ? 'Var' : 'Yok'}</StatusBadge>
            {has && (
              <span style={{ fontSize: 10, color: 'var(--text-secondary, #6b7280)' }}>{restrictionSummary(row.original)}</span>
            )}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'usage', header: 'Kullanım', enableColumnFilter: false,
      cell: ({ row }) => {
        const c = Number(row.original.usageCount || 0);
        if (!c) return <span style={{ fontSize: 12, color: 'var(--text-secondary, #9ca3af)' }}>—</span>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <StatusBadge tone="ok">{c} kullanım</StatusBadge>
            <span style={{ fontSize: 10, color: 'var(--text-secondary, #6b7280)' }}>{fmtMoney(row.original.usageAmount)} ₺</span>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'created', header: 'Oluşturma',
      cell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, fontSize: 12 }}>
          <span>{fmtDate(row.original.createdDate)}</span>
          <span style={{ fontSize: 10, color: 'var(--text-secondary, #6b7280)' }}>{row.original.createdBy || '-'}</span>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Düzenle" aria-label="Düzenle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [productLabels, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('UpdatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = () => {
    try {
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const cols = [
        { field: 'code', headerName: 'Kod' },
        { field: 'name', headerName: 'Ad' },
        { headerName: 'Tür', valueGetter: ({ row }) => deriveCampaignKind(row).label },
        { headerName: 'Koşul', valueGetter: ({ row }) => buildHumanRule(row) },
        { headerName: 'Başlangıç', valueGetter: ({ row }) => fmtDate(row.startDate) },
        { headerName: 'Bitiş', valueGetter: ({ row }) => fmtDate(row.endDate) },
        { headerName: 'Durum', valueGetter: ({ row }) => (row.isActive ? 'Aktif' : 'Pasif') },
        { headerName: 'Kullanım', field: 'usageCount' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `kampanyalar_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod / ad / açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Kampanya Yönetimi"
        subtitle="Saha sipariş ekranında otomatik uygulanan kampanyaları oluşturun, filtreleyin ve yönetin."
        actions={(
          <>
            <ViewSwitch value={viewMode} onChange={setViewMode} modes={CAMPAIGN_VIEW_MODES} />
            {canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Ekle</button>}
          </>
        )}
        toolbar={toolbarNode}
        footer={viewMode === 'chart' ? null : footerNode}
      >
        {viewMode === 'chart' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <CampaignChartView rows={filteredSorted} affectedCustomersTotal={affectedCustomersTotal} />
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'card' ? (
          <C360CardGrid
            rows={table.getRowModel().rows.map((r) => r.original)}
            card={(row) => ({
              avatarName: row.name,
              avatarTone: 'rep',
              title: row.name,
              subtitle: row.code,
              statusTone: row.isActive ? 'ok' : 'neutral',
              statusLabel: row.isActive ? 'Aktif' : 'Pasif',
              meta: [
                { k: 'Tür', v: deriveCampaignKind(row).label },
                (row.startDate || row.endDate) ? { k: 'Tarih', v: `${fmtDate(row.startDate)} – ${fmtDate(row.endDate)}` } : null,
                Number(row.usageCount) ? { k: 'Kullanım', v: Number(row.usageCount).toLocaleString('tr-TR') } : null,
              ].filter(Boolean),
            })}
            actions={(row) => [
              { kind: 'view', title: 'Görüntüle', onClick: openDetail },
              { kind: 'edit', title: 'Düzenle', onClick: openDetail, hidden: !canEdit },
              { kind: 'delete', title: 'Sil', onClick: (r) => { setSelected(r); setDeleteConfirm(true); }, hidden: !canDelete },
            ]}
            onCardClick={setSelected}
            onCardDoubleClick={openDetail}
            selectedId={selected?.id}
            loading={loading}
          />
        ) : (
          <C360Table
            table={table}
            columnFilterMeta={COLUMN_FILTER_META}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            loading={loading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={openDetail}
            emptyText="Kayıt bulunamadı"
          />
        )}
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected.name}"</strong> kampanyasını silmek istediğinize emin misiniz?</p>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#dc2626' }}>Mobile sipariş ekranında bu kampanya bir sonraki sync'te artık uygulanmayacak.</p>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btnDanger, opacity: saving ? 0.6 : 1 }} onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor...' : 'Evet, Sil'}</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default CampaignsPage;
